// Companies (parts/Companies.body): the heading with the store's own counts, the search
// box, the filter pills, the table with the hierarchy indented (D76), the owner with its
// source (N12), and the footer line. A row opens into CompanyRecord. The last-contact column
// arrives from the bounded pass after the table has rendered (records.ts).
//
// The find searches as you type, over the name, the group above it and every spelling on record (pipeline F1,
// B1950); the find and the chips are kept in the address, so a reload or Back draws the same list. The list wears the
// filter bar every list wears (pipeline F5, B1957), with By owner and By product: a product is a family of the Deals
// rings, and a company has it when an opportunity still on the Deals page or a contract names it. Their options and
// counts are the API's, for the whole store.
import { records, type CompanyFilter, type CompanyRow, type LastContactMap } from '../api.js';
import { FilterBar as FindBar, CompanyName, CompanyLogo as Logo } from '../parts/index.js';
import { oneOf, useFilterParams } from '../find/params.js';
import { contactWord, Count, Empty, Facet, Failed, Foot, Heading, Loading, orText, Pill, RowLink, routeWord, typeWord, useLoad, type PageProps } from './shared.js';

const FILTERS: readonly CompanyFilter[] = ['all', 'clients', 'prospects', 'mine', 'owner_not_set', 'owner_proposed'];
const KEYS = ['q', 'show', 'market', 'type', 'owner', 'product'] as const;

export function ownerCell(c: CompanyRow): { name: string; note: string } {
  if (c.ownerName || c.ownerPrincipalId) return { name: c.ownerName ?? c.ownerPrincipalId!, note: c.ownerSource ?? '' };
  if (c.proposed) return { name: c.proposed.name ?? c.proposed.principalId, note: 'suggested, writes to them most' };
  return { name: 'Not set', note: '' };
}

export function Companies({ store, open }: PageProps) {
  const [p, set] = useFilterParams(KEYS);
  const { q, market, type, owner, product } = p;
  const filter = oneOf(p.show, FILTERS, 'all');
  const setFilter = (f: CompanyFilter) => set({ show: f === 'all' ? '' : f });
  const [state, , busy] = useLoad(() => records.companies(store.tenantId, { q, filter, market, type, owner, product }), [store.tenantId, q, filter, market, type, owner, product], { keep: true });
  const [contact] = useLoad<LastContactMap>(() => records.lastContact(store.tenantId), [store.tenantId]);
  const lc = contact.kind === 'ready' ? contact.data : null;

  const counts = state.kind === 'ready' ? state.data.counts : null;
  const facets = state.kind === 'ready' ? state.data.facets : null;
  const sheet = state.kind === 'ready' ? state.data.sheetFields : true;
  return (
    <div className="recs">
      <Heading title="Companies" line={<>
        <Count n={counts?.all}>on record</Count>
        <Count n={counts?.clients}>clients</Count>
        <Count n={counts?.ownerNotSet}>with no owner</Count>
      </>} />
      <FindBar find="Find a company" q={q} onFind={(v) => set({ q: v })}
        shown={state.kind === 'ready' && !busy ? state.data.companies.length : null}   // BUGS.md D25: not the old list's while the next is read
        total={counts?.all ?? null} noun={['company', 'companies']}
        onClearAll={() => set({ q: '', show: '', market: '', type: '', owner: '', product: '' })}
        extraSet={filter !== 'all' || market !== '' || type !== ''}
        extra={<>
          <span className="chips-l">Show</span>
          <Pill on={filter === 'all'} onClick={() => setFilter('all')}>All</Pill>
          <Pill on={filter === 'clients'} onClick={() => setFilter('clients')} n={counts?.clients}>Clients</Pill>
          <Pill on={filter === 'prospects'} onClick={() => setFilter('prospects')} n={counts?.prospects}>Prospects</Pill>
          <Pill on={filter === 'mine'} onClick={() => setFilter('mine')}>Mine</Pill>
          <Pill on={filter === 'owner_not_set'} onClick={() => setFilter('owner_not_set')} n={counts?.ownerNotSet}>Owner not set</Pill>
          <Pill on={filter === 'owner_proposed'} onClick={() => setFilter('owner_proposed')} n={counts?.ownerProposed}>Owner proposed</Pill>
          <Facet label="By market" value={market} options={facets?.markets ?? []} onChange={(v) => set({ market: v })} />
          <Facet label="By type" value={type} options={facets?.types ?? []} onChange={(v) => set({ type: v })} />
        </>}
        picks={[
          { label: 'By owner', find: 'Find a person', any: 'Anyone', value: owner, options: facets ? facets.owners ?? [] : null, onChange: (v) => set({ owner: v }) },
          { label: 'By product', find: 'Find a product', any: 'Any product', value: product, options: facets ? facets.products ?? [] : null, onChange: (v) => set({ product: v }) },
        ]} />
      {/* "Add a company" is drawn on the artboard; creating one is the seeds' or a later step's (a company is created from the sheet or by hand on the record, N16). */}
      {state.kind === 'loading' ? <Loading what="the companies" /> : state.kind === 'error' ? <Failed message={state.message} /> : (
        <div className={busy ? 'scroll reading' : 'scroll'} aria-busy={busy}>
          <table className="rec cos">
            <thead><tr><th>Company</th><th>Type</th><th>Owner</th><th>Tier</th><th>HQ</th><th>Route</th><th>Client / prospect</th><th className="num">Open deals</th><th>Last contact</th></tr></thead>
            <tbody>
              {state.data.companies.length === 0
                ? <Empty cols={9} head="No company matches that." says="Clear the search, or choose All. Companies come from the register and from the mail Cobrain has read; a new one is added on a company's own record." />
                : null}
              {state.data.companies.map((c) => {
                const o = ownerCell(c);
                const child = c.parentId !== null;
                return (
                  <tr key={c.id} className="row-open" onClick={() => open(`/companies/${c.id}`)}>
                    <td className={child ? 'indent' : undefined}>
                      {/* The company's stored logo before its name, at the line's size, or its initials (pipeline RC17, U375). */}
                      <RowLink className="row-link co-with-logo" to={`/companies/${c.id}`} open={open}><Logo name={c.name} logo={c.logo ?? null} size="sm" />{child ? c.name : <b>{c.name}</b>}</RowLink>
                      {c.childCount > 0 ? <><br /><span className="note">{c.childCount} {c.childCount === 1 ? 'brand' : 'brands'} under it</span></> : null}
                      {child ? <><br /><span className="note">{c.companyTier === 'Operating Brand' ? 'brand' : orText(c.companyTier).toLowerCase()}{c.parentName ? <>, {c.parentId ? <span onClick={(e) => e.stopPropagation()}><CompanyName id={c.parentId} name={c.parentName} go={open} /></span> : c.parentName}</> : ''}</span></> : null}
                    </td>
                    <td>{orText(c.companyType)}</td>
                    <td>{o.name}{o.note ? <> <span className="note">{o.note}{c.proposed && !c.ownerPrincipalId ? <span className="dotted cell-act">Accept</span> : null}</span></> : null}</td>
                    <td>{sheet ? orText(c.accountTier) : <span className="ph">—</span>}</td>
                    <td>{sheet ? orText(c.hqMarket) : <span className="ph">—</span>}</td>
                    <td>{sheet ? routeWord(c.integrationRoute) : <span className="ph">—</span>}</td>
                    <td>{typeWord(c.accountType)}</td>
                    <td className="num">{c.openDeals > 0 ? c.openDeals : ''}{c.pausedDeals > 0 ? <><br /><span className="note">{c.pausedDeals} paused</span></> : null}</td>
                    <td>{lc ? contactWord(lc.organisations[c.id]) : <span className="note">…</span>}</td>
                  </tr>
                );
              })}
              <Foot cols={9} offset={0} limit={state.data.companies.length} total={counts?.all ?? state.data.companies.length} onPage={null} />
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
